import { useCallback } from "react";
import { useLocalStorage } from "@/hooks/use-local-storage";

export const POS_CART_STORAGE_KEY = "pos-cart";

export type PosCartItem = {
  productId: string;
  variantId: string;
  name: string;
  imageUrl?: string | null;
  price: number;
  quantity: number;
};

type ItemChange = {
  variantId: string;
  quantity: number;
  item?: Omit<PosCartItem, "quantity">;
};

function applyItemChange(
  items: PosCartItem[],
  change: ItemChange,
  mode: "add" | "replace"
): PosCartItem[] {
  const { variantId, quantity, item } = change;
  const existing = items.find((i) => i.variantId === variantId);

  if (mode === "replace" && quantity <= 0) {
    return items.filter((i) => i.variantId !== variantId);
  }

  if (existing) {
    const newQty = mode === "add" ? existing.quantity + quantity : quantity;
    if (newQty <= 0) {
      return items.filter((i) => i.variantId !== variantId);
    }
    return items.map((i) =>
      i.variantId === variantId ? { ...i, quantity: newQty } : i
    );
  }

  if (!item || quantity <= 0) return items;
  return [...items, { ...item, quantity }];
}

export function usePosCart() {
  const { state, update } = useLocalStorage(POS_CART_STORAGE_KEY, {
    items: [] as PosCartItem[],
  });
  const items: PosCartItem[] = state?.items ?? [];

  const addItem = useCallback(
    (item: Omit<PosCartItem, "quantity">, quantity = 1) => {
      const newItems = applyItemChange(
        items,
        { item, variantId: item.variantId, quantity },
        "add"
      );
      update("items", newItems);
    },
    [items, update]
  );

  const addItemsBatch = useCallback(
    (
      entries: Array<{
        item: Omit<PosCartItem, "quantity">;
        quantity: number;
      }>
    ) => {
      let newItems = [...items];
      for (const { item, quantity } of entries) {
        newItems = applyItemChange(
          newItems,
          { item, variantId: item.variantId, quantity },
          "add"
        );
      }
      update("items", newItems);
    },
    [items, update]
  );

  const syncItemsBatch = useCallback(
    (
      entries: Array<{
        item: Omit<PosCartItem, "quantity">;
        quantity: number;
      }>
    ) => {
      let newItems = [...items];
      for (const { item, quantity } of entries) {
        newItems = applyItemChange(
          newItems,
          { item, variantId: item.variantId, quantity },
          "replace"
        );
      }
      update("items", newItems);
    },
    [items, update]
  );

  const removeItem = useCallback(
    (variantId: string) => {
      const newItems = applyItemChange(
        items,
        { variantId, quantity: 0 },
        "replace"
      );
      update("items", newItems);
    },
    [items, update]
  );

  const updateQty = useCallback(
    (variantId: string, quantity: number) => {
      const existing = items.find((i) => i.variantId === variantId);
      const newItems = applyItemChange(
        items,
        { variantId, quantity, item: existing },
        "replace"
      );
      update("items", newItems);
    },
    [items, update]
  );

  const clear = useCallback(() => {
    update("items", []);
  }, [update]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const getQuantity = useCallback(
    (variantId: string) => {
      const item = items.find((i) => i.variantId === variantId);
      return item?.quantity ?? 0;
    },
    [items]
  );

  return {
    items,
    addItem,
    addItemsBatch,
    syncItemsBatch,
    removeItem,
    updateQty,
    clear,
    itemCount,
    subtotal,
    getQuantity,
  };
}
